import { useContext, useEffect, useRef, useState } from 'react'
import { Box, Button, FormHelperText, IconButton, Tooltip, Typography } from '@mui/material';
import Title from '../custom/Title';
import DynamicForm, { TDynamicFieldProps, TField, useDynamicForm } from '../forms/DynamicFormNew';

import { TransactionContext } from '../../contexts/TransactionContext';
import { NotificationContext } from '../../contexts/NotificationContext';
import useService from '../../hooks/useService';
import Divider from '../custom/Divider';
import { IDelivery } from '../../Models/Delivery';
import Modal from '../Modals';
import { createDelivery, createUserAuthLetter, deleteUserAuthLetter, getDelivery, getUserAuthLetter } from '../../services/Transactions';
import { CustomerContext } from '../../contexts/CustomerContext';
import { GridCloseIcon } from '@mui/x-data-grid';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { TCustomerFields } from '../../Models/Inbound';

type TCustomerForm = TCustomerFields & {
  chargesDetails: string;
  GMSCharges: string;
  userDocuments: string;
};

const UploadDocument = (props: TDynamicFieldProps<TCustomerForm>) => {
  const { execute, store } = useService();
  const { transactionsStore, notificationStore } = store;
  const { transactionInfo, userAuthFiles } = transactionsStore;
  const { customerConfig } = useContext(CustomerContext)
  const { notify } = notificationStore;
  const { error } = props;

  const [hoveredFile, setHoveredFile] = useState('');
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // ---------- file → base64 ----------
  const fileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
    });

  // ---------- upload ----------
  const uploadFile = async (file: File) => {
    const base64 = await fileToBase64(file);

    const { data, error } = await execute(() =>
      createUserAuthLetter({
        dgCustomerRefNo: customerConfig.dgCustomerRefNo!,
        authFile: base64,
        transactionType: transactionInfo.transactionType,
      })
    );

    if (!error && data) notify('File Uploaded.');
  };

  // ---------- delete ----------
  const deleteFile = async (id: string) => {
    const { data, error } = await execute(() =>
      deleteUserAuthLetter({
        id,
        dgCustomerRefNo: customerConfig.dgCustomerRefNo!,
        transactionType: transactionInfo.transactionType,
      })
    );

    if (!error && data) notify('File Deleted.');
  };

  // ---------- download ----------
  const downloadFile = (fileUrl: string, idx: number) => {
    const anchor = document.createElement('a');
    anchor.href = fileUrl;
    anchor.download = `${transactionInfo.globalTransactionNumber!}-${idx + 1}`;
    anchor.click();
    anchor.remove();
  };

  // ---------- input change ----------
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length) return;

    for (const file of Array.from(files)) {
      await uploadFile(file);
    }

    e.target.value = '';
  };

  const isPdf = (url: string) => /\.pdf($|\?)/i.test(url);

  return (
    <>
      <div>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => fileInputRef.current?.click()}
        >
          {userAuthFiles.length > 0
            ? 'Add More'
            : 'Upload File'}
        </Button>

        <input
          ref={fileInputRef}
          type="file"
          hidden
          multiple
          accept="image/*,.pdf"
          onChange={handleFileChange}
        />

        {/* ---------- grid ---------- */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
            gap: '10px', 
            marginTop: '15px',
            justifyContent: 'flex-start',
          }}
        >
          {userAuthFiles.map((elem, idx) => (
            <div
                key={elem.id}
                style={{
                    marginRight: "15px",
                    position: "relative",
                    width: 100,
                }}
                >
                {/* Delete Button */}
                <IconButton
                    size="small"
                    onClick={() => deleteFile(elem.id)}
                    sx={{
                    position: "absolute",
                    top: -8,
                    right: -8,
                    bgcolor: "white",
                    boxShadow: 1,
                    zIndex: 10,
                    "&:hover": { bgcolor: "#f5f5f5" },
                    }}
                >
                    <GridCloseIcon fontSize="small" />
                </IconButton>

                {/* Preview Box */}
                <Box
                    sx={{
                    width: 100,
                    height: 100,
                    border: "1px solid #ddd",
                    borderRadius: 1,
                    overflow: "hidden",
                    bgcolor: "#fafafa",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    cursor: "pointer",
                    "&:hover .download-btn": {
                        opacity: 1,
                    },
                    }}
                >
                    {/* Preview */}
                    {isPdf(elem.authFile) ? (
                        <Box
                            sx={{
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: "#fff4f4",
                            border: "1px solid #f5c2c2",
                            borderRadius: 1,
                            }}
                        >
                            <PictureAsPdfIcon
                                sx={{ fontSize: 42, mb: 0.5 }}
                                color="error"
                            />
                            <Typography
                                variant="caption"
                                fontWeight={600}
                                color="error"
                            >
                                PDF
                            </Typography>
                        </Box>
                        ) : (
                        <img
                            src={elem.authFile}
                            alt="preview"
                            style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            }}
                        />
                    )}
                    {/* Download */}
                    <Tooltip title="Download">
                    <IconButton
                        className="download-btn"
                        size="small"
                        onClick={() =>
                        downloadFile(elem.authFile, idx)
                        }
                        sx={{
                        position: "absolute",
                        bottom: 4,
                        right: 4,
                        bgcolor: "white",
                        opacity: 0,
                        transition: "0.2s",
                        boxShadow: 1,
                        "&:hover": { bgcolor: "#f5f5f5" },
                        }}
                    >
                        <DownloadIcon fontSize="small" />
                    </IconButton>
                    </Tooltip>
                </Box>
            </div>
          ))}
        </div>
      </div>

      {error && <FormHelperText error>{error}</FormHelperText>}
    </>
  );
};

export const CREATE_DELIVERY_CONFIG: TField<IDelivery & { userDocuments: string }>[] = [
    // {
    //     fieldName: 'deliveryStatus',
    //     isVisible: true,
    //     fieldType: 'text',
    //     label: 'Delivery Status',
    //     fullWidth: false
    // },
    // {
    //     fieldName: 'deliveryType',
    //     isVisible: true,
    //     fieldType: 'text',
    //     label: 'Delivery Type',
    //     fullWidth: false
    // },
    {
        fieldName: 'shipmentDate',
        isVisible: true,
        fieldType: 'date',
        label: 'Shipment Date',
        fullWidth: false,
        validation: {
            required: true
        }
    },
    {
        fieldName: 'deliveryMethod',
        isVisible: false,
        fieldType: 'select',
        label: 'Delivery Method',
        fullWidth: false,
        options: [{ value: "pickup", label: 'Pickup' }, { value: "doorstepDelivery", label: 'Deliver to doorstep' }],
    },
    {
        fieldName: 'transporter',
        isVisible: false,
        fieldType: 'select',
        label: 'Transporter',
        fullWidth: false,
        options: [{ value: "ABC", label: 'ABC' }, { value: "XYZ", label: 'XYZ' }],
    },
    // {
    //     fieldName: 'direction',
    //     isVisible: false,
    //     fieldType: 'autocomplete',
    //     label: 'Direction',
    //     fullWidth: false,
    //     options: [{ value: "o", label: 'Out' }, { value: "i", label: 'In' }],
    //     disabled:true
    // },
    // {
    //     fieldName: 'physicalOrderNumber',
    //     isVisible: true,
    //     fieldType: 'text',
    //     label: 'Physical Order No.',
    //     fullWidth: false
    // },
    {
        fieldName: 'exciseInvoiceNumber',
        isVisible: true,
        fieldType: 'text',
        label: 'Excise Invoice No.',
        fullWidth: false
    },
    {
        fieldName: 'trackingNumber',
        isVisible: true,
        fieldType: 'text',
        label: 'Tracking No',
        fullWidth: false,
        disabled: false
    },

    {
        fieldName: 'shippingCharge',
        isVisible: false,
        fieldType: 'number',
        label: 'Shipping Charge',
        fullWidth: false,
        disabled: true
    },
    {
        fieldName: 'tax',
        isVisible: false,
        fieldType: 'number',
        label: 'Tax',
        fullWidth: false
    },
    {
        fieldName: 'serialNumbers',
        isVisible: true,
        fieldType: 'text',
        label: 'Serial Numbers',
        fullWidth: false,
        disabled: true
    },
    {
        fieldName: 'remarks',
        isVisible: true,
        fieldType: 'text',
        label: 'Remarks',
        fullWidth: false
    },
    {
        fieldName: 'userDocuments',
        isVisible: true,
        fieldType: 'custom',
        component: UploadDocument,
        label: 'Image',
        width: '100%',
        fullWidth: true,
        validation: { required: false }
    }
]

function DeliveryLineDetails({ lineIds, deliveryState, closeDeliveryForm }: { deliveryState?: any, lineIds: string[], closeDeliveryForm: () => any }) {
    const { physicalOrder, transactionInfo: { globalTransactionNumber, transactionType } } = useContext(TransactionContext)
    const { notify } = useContext(NotificationContext)
    const { execute } = useService()
    const { registerForm, formData: formData, setFormData } = useDynamicForm({
        formConfig: CREATE_DELIVERY_CONFIG
    })
    const { customerConfig } = useContext(CustomerContext)
    const { dgCustomerRefNo } = customerConfig

    useEffect(() => {
        setFormData(prev => ({ ...prev, serialNumbers: JSON.stringify(lineIds) }))
        if (physicalOrder.isDoorstepDelivery)
            setFormData(prev => ({ ...prev, transporter: "none" }))
    }, [])


    const submitHandler = async (data: any) => {
        let payload = {
            globalTransactionNumber,
            lineIds,
            transactionType: transactionType,
            status: data.deliveryStatus,
            shipmentDate: data.shipmentDate,
            transporter: data.transporter,
            awbNo: data.trackingNumber,
            exciseInNo: data.exciseInvoiceNumber,
            remarks: data.remarks
        }

        const { error } = await execute(() => createDelivery(payload))
        if (error === null) {
            notify(deliveryState ? 'Delivery Updated.' : 'Delivery Created.')
            await execute(() => getDelivery({ globalTransactionNumber, transactionType }))

            closeDeliveryForm()
        }
    }

    const fetchUserAuthFiles = async () => {
        const { data, error } = await execute(() => getUserAuthLetter({ dgCustomerRefNo: dgCustomerRefNo }));
    }

    useEffect(() => {
        fetchUserAuthFiles()
    }, [])

    return (
        <div style={{ padding: '0 10px' }}>
            <div style={{ borderBottom: '1px solid #d8d8d8' }}>
                <Title style={{ textAlign: 'left', margin: '20px 10px 10px 10px', color: '#7f7d76' }}>Add Delivery Line Details</Title>
            </div>
            <Modal onClose={closeDeliveryForm} open={true} title="create delivery" >
                <DynamicForm {...registerForm()} onSubmit={submitHandler} submitText='Save' />
            </Modal>
            <Divider />
            <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', marginTop: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'flex-start', gap: '10px' }}>
                    <Button variant='outlined' type='submit' color='secondary' onClick={closeDeliveryForm} >{'Close'}</Button>
                </div>
            </div>
        </div>
    )
}

export default DeliveryLineDetails